// src/pages/customerComponents/RemoteBusinessComponents/AffiliateSignup.js

import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import '../../../styles/CustomerSiteStyles/AffiliateSignup.css';

// ─── Config ────────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_nwwqbpi';
const EMAILJS_TEMPLATE_ID = 'template_gnoho8n';
const EMAILJS_PUBLIC_KEY  = 'WDj2Vkb5uvjrLg-D-';

const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/21939092/4yyq2t2/';

const submitToZapier = async (formData) => {
  const nameParts = formData.from_name.trim().split(' ');
  const payload = {
    first_name:    nameParts[0] || '',
    last_name:     nameParts.slice(1).join(' ') || '',
    email:         formData.from_email,
    phone:         formData.phone,
    address_line1: formData.address_line1,
    address_line2: formData.address_line2,
    city:          formData.city,
    state:         formData.state,
    zip:           formData.zip,
    message:       formData.message,
    job_name:      `${formData.from_name} - Affiliate Application`,
    job_type:      'Non-Revenue',
    job_status:    'Affiliate Marketer',
  };

  const res = await fetch(ZAPIER_WEBHOOK_URL, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Zapier webhook failed');
};

// ─── Data ───────────────────────────────────────────────────────────────────
const perks = [
  {
    icon: '🗓️',
    title: 'Flexible Schedule',
    body: 'Refer on your own time — no quotas, no clocking in. Fit it around your existing work and life without skipping a beat.',
  },
  {
    icon: '🛡️',
    title: 'Risk-Free Earnings',
    body: "Zero setup fees. Zero commitment. Zero pressure to keep referring. You only benefit — there's nothing to lose.",
  },
  {
    icon: '🏡',
    title: 'Homeowner Partnership Program',
    body: 'Eligible homeowners who join can qualify for up to $750 off their own roof or solar project — just for being part of the program.',
  },
  {
    icon: '📈',
    title: 'Performance-Based Income',
    body: 'Earn $300 for every qualifying lead that turns into a completed build. No cap — the more people you refer, the more you earn.',
  },
  {
    icon: '🤝',
    title: 'Support System',
    body: "Our team is always reachable. Have a question about a referral or a homeowner who needs help? We've got your back.",
  },
  {
    icon: '🔑',
    title: 'Exclusive Resources',
    body: 'Access marketing materials, talking points, and insider tools that make referring effortless and effective.',
  },
];

const idealFor = [
  { icon: '🏡', label: 'Real Estate Agents' },
  { icon: '✂️', label: 'Hairstylists' },
  { icon: '💪', label: 'Personal Trainers' },
  { icon: '🍽️', label: 'Restaurant Owners' },
  { icon: '⛪', label: 'Community Leaders' },
  { icon: '🧑‍💼', label: 'Insurance Agents' },
  { icon: '🧘', label: 'Wellness Coaches' },
  { icon: '🎓', label: 'Educators' },
];

// ─── Fade-in hook ───────────────────────────────────────────────────────────
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Perk Card ──────────────────────────────────────────────────────────────
const PerkCard = ({ perk, delay }) => {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={`af-perk-card ${visible ? 'af-perk-card--visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="af-perk-card__icon">{perk.icon}</div>
      <h3 className="af-perk-card__title">{perk.title}</h3>
      <p className="af-perk-card__body">{perk.body}</p>
    </div>
  );
};

// ─── Validation ──────────────────────────────────────────────────────────────
const validate = (name, value) => {
  const v = (value || '').trim();
  switch (name) {
    case 'from_name':
      return v ? '' : 'Name is required.';
    case 'from_email':
      if (!v) return 'Email is required.';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Enter a valid email.';
    case 'phone':
      if (!v) return 'Phone is required.';
      return /^\d{10}$/.test(v) ? '' : 'Must be exactly 10 digits.';
    default:
      return '';
  }
};

// ─── Field wrapper — tooltip only renders when there IS an error ─────────────
const Field = ({ id, label, required, error, className = '', children }) => (
  <div className={`af-form-group ${className}`}>
    <label htmlFor={id}>
      {label}{required && <span className="af-required"> *</span>}
    </label>
    {/* af-input-wrap is ALWAYS the same height — tooltip floats above via absolute */}
    <div className="af-input-wrap">
      {error && (
        <div className="af-tooltip" role="alert">
          {error}
          <span className="af-tooltip__arrow" />
        </div>
      )}
      {children}
    </div>
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const AffiliateSignup = () => {
  const [form, setForm] = useState({
    from_name: '', from_email: '', phone: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    message: '',
  });

  // Only show errors for fields the user has already blurred, OR after a submit attempt
  const [touched, setTouched]   = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus]     = useState('idle'); // idle | sending | success | error
  const [heroRef, heroVisible]  = useFadeIn(0.05);

  // Returns error string only if the field should currently show it
  const getError = (name) => {
    if (!submitted && !touched[name]) return '';
    return validate(name, form[name]);
  };

  const handleChange = (e) => {
    const { name, value: raw } = e.target;
    // Strip non-digits and cap at 10 for phone
    const value = name === 'phone' ? raw.replace(/\D/g, '').slice(0, 10) : raw;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    const hasErrors = ['from_name', 'from_email', 'phone'].some(
      (f) => validate(f, form[f])
    );
    if (hasErrors) return;

    setStatus('sending');
    try {
      await submitToZapier(form);
      // Fire email notification in background — don't block success on it
      emailjs
        .send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, form, EMAILJS_PUBLIC_KEY)
        .catch(() => {});
      setStatus('success');
      setForm({ from_name: '', from_email: '', phone: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', message: '' });
      setTouched({});
      setSubmitted(false);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="af-page">

      {/* ── Hero ── */}
      <section ref={heroRef} className={`af-hero ${heroVisible ? 'af-hero--visible' : ''}`}>
        <div className="af-hero__eyebrow">The Elevate Program</div>
        <h1 className="af-hero__headline">
          Turn Your Network <br />
          <span className="af-hero__accent">Into Income.</span>
        </h1>
        <p className="af-hero__sub">
          You already know people. We'll pay you every time one of them needs a new roof or solar
          panels. No experience. No risk. No ceiling.
        </p>
        <div className="af-hero__stat-row">
          <div className="af-hero__stat">
            <span className="af-hero__stat-num">$300</span>
            <span className="af-hero__stat-label">per qualifying lead</span>
          </div>
          <div className="af-hero__stat-divider" />
          <div className="af-hero__stat">
            <span className="af-hero__stat-num">No Cap</span>
            <span className="af-hero__stat-label">on your earnings</span>
          </div>
          <div className="af-hero__stat-divider" />
          <div className="af-hero__stat">
            <span className="af-hero__stat-num">$0</span>
            <span className="af-hero__stat-label">to join, forever</span>
          </div>
        </div>
      </section>

      {/* ── Why Join ── */}
      <section className="af-perks-section">
        <div className="af-section-header">
          <div className="af-tag">Why Join</div>
          <h2 className="af-section-title">Everything Working in Your Favor</h2>
          <p className="af-section-sub">
            We built this program to be a genuine win for the people who join it — not a side hustle with fine print.
          </p>
        </div>
        <div className="af-perks-grid">
          {perks.map((perk, i) => (
            <PerkCard key={perk.title} perk={perk} delay={i * 70} />
          ))}
        </div>
      </section>

      {/* ── Ideal For ── */}
      <section className="af-ideal-section">
        <div className="af-section-header">
          <div className="af-tag">Who It's For</div>
          <h2 className="af-section-title">Perfect If You Already Know People</h2>
          <p className="af-section-sub">
            If your job or lifestyle puts you in front of homeowners, you're already halfway there.
          </p>
        </div>
        <div className="af-ideal-grid">
          {idealFor.map((item) => (
            <div key={item.label} className="af-ideal-chip">
              <span className="af-ideal-chip__icon">{item.icon}</span>
              <span className="af-ideal-chip__label">{item.label}</span>
            </div>
          ))}
        </div>
        <p className="af-ideal-note">…and anyone else whose world is built on relationships and trust.</p>
      </section>

      {/* ── How It Works ── */}
      <section className="af-how-section">
        <div className="af-section-header">
          <div className="af-tag">How It Works</div>
          <h2 className="af-section-title">Three Steps to Your First Check</h2>
        </div>
        <div className="af-how-steps">
          {[
            { n: '1', title: 'Sign Up Below', desc: "Fill out the short form. We'll reach out to get you onboarded and answer any questions." },
            { n: '2', title: 'Share the Word', desc: 'Use our materials — or just talk to people you know — and send interested homeowners our way.' },
            { n: '3', title: 'Collect Your Cut', desc: 'When a qualifying lead turns into a completed build, you earn $300. No cap, no chasing, no waiting.' },
          ].map((s) => (
            <div key={s.n} className="af-how-step">
              <div className="af-how-step__num">{s.n}</div>
              <h3 className="af-how-step__title">{s.title}</h3>
              <p className="af-how-step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Signup Form ── */}
      <section className="af-form-section">
        <div className="af-section-header">
          <div className="af-tag">Get Started</div>
          <h2 className="af-section-title">Join the Program Today</h2>
          <p className="af-section-sub">
            Fill out the form and a member of our team will reach out within one business day to get you set up.
          </p>
        </div>

        <div className="af-form-card">
          {status === 'success' ? (
            <div className="af-form-success">
              <div className="af-form-success__icon">🎉</div>
              <h3>You're on the list!</h3>
              <p>
                We received your information — welcome to the Elevate family! You should be
                receiving a welcome email shortly. Please monitor your inbox and check your spam
                folder if you don't see it right away.
              </p>
            </div>
          ) : (
            <form className="af-form" onSubmit={handleSubmit} noValidate>

              <div className="af-form-row">
                <Field id="from_name" label="Full Name" required error={getError('from_name')}>
                  <input
                    id="from_name" name="from_name" type="text" placeholder="Jane Smith"
                    value={form.from_name} onChange={handleChange} onBlur={handleBlur}
                  />
                </Field>
                <Field id="from_email" label="Email Address" required error={getError('from_email')}>
                  <input
                    id="from_email" name="from_email" type="email" placeholder="jane@example.com"
                    value={form.from_email} onChange={handleChange} onBlur={handleBlur}
                  />
                </Field>
              </div>

              <div className="af-form-row">
                <Field id="phone" label="Phone Number" required error={getError('phone')}>
                  <input
                    id="phone" name="phone" type="tel" placeholder="5550001234"
                    value={form.phone} onChange={handleChange} onBlur={handleBlur}
                    maxLength={10}
                  />
                </Field>
                <Field id="address_line1" label="Address Line 1" error="">
                  <input
                    id="address_line1" name="address_line1" type="text"
                    placeholder="123 Main St"
                    value={form.address_line1} onChange={handleChange}
                  />
                </Field>
              </div>

              <div className="af-form-row">
                <Field id="address_line2" label="Address Line 2" error="" className="af-form-group--full">
                  <input
                    id="address_line2" name="address_line2" type="text"
                    placeholder="Apt, Suite, Unit (optional)"
                    value={form.address_line2} onChange={handleChange}
                  />
                </Field>
              </div>

              <div className="af-form-row--3">
                <Field id="city" label="City" error="">
                  <input
                    id="city" name="city" type="text" placeholder="Chicago"
                    value={form.city} onChange={handleChange}
                  />
                </Field>
                <Field id="state" label="State" error="">
                  <input
                    id="state" name="state" type="text" placeholder="IL"
                    value={form.state} onChange={handleChange}
                    maxLength={2}
                  />
                </Field>
                <Field id="zip" label="ZIP Code" error="">
                  <input
                    id="zip" name="zip" type="text" placeholder="60601"
                    value={form.zip} onChange={handleChange}
                    maxLength={5}
                  />
                </Field>
              </div>

              <div className="af-form-group af-form-group--full">
                <label htmlFor="message">Anything you'd like us to know?</label>
                <div className="af-input-wrap">
                  <textarea
                    id="message" name="message" rows={4}
                    placeholder="Tell us about yourself, your network, or any questions you have…"
                    value={form.message} onChange={handleChange}
                  />
                </div>
              </div>

              {status === 'error' && (
                <p className="af-form-error">
                  Something went wrong. Please try again or email us at{' '}
                  <a href="mailto:affordableroofingplussolar@gmail.com">
                    affordableroofingplussolar@gmail.com
                  </a>.
                </p>
              )}

              <button type="submit" className="af-submit-btn" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Apply to Join →'}
              </button>

              <p className="af-form-disclaimer">
                No fees. No commitment. Just opportunity. We'll never share your information.
              </p>
            </form>
          )}
        </div>
      </section>

    </div>
  );
};

export default AffiliateSignup;